import React, { useEffect, useRef, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import { useInView } from '../../hooks/useInView';
import { BasketIcon } from '../BasketIcon';

const BASKET_NAME_CAP = 5;
const REASSURANCES = [
  'One topic is enough',
  'No match needed',
  'Move it anytime',
];

// Same primary treatment as the feedback Send button.
const PRIMARY_BUTTON_CLASS_NAME = `
  inline-flex items-center justify-center
  min-h-11 w-full sm:w-auto rounded-lg px-5 font-bold
  border-2 border-black bg-[#9f1239] text-white
  focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9f1239] focus-visible:ring-offset-2
  disabled:cursor-not-allowed disabled:opacity-60
`;

const BasketGlyph = () => (
  <span className="inline-flex shrink-0 [&_svg]:mr-0 [&_svg]:h-5 [&_svg]:w-5" aria-hidden="true">
    <BasketIcon />
  </span>
);

const ExampleField = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-neutral-500">{label}</p>
    <p className="mt-1 text-sm text-neutral-900">{value}</p>
  </div>
);

const StepHeading = ({ number, title }) => (
  <div className="mb-3 flex items-start gap-2">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-sm font-semibold text-white">
      {number}
    </span>
    <h3 className="pt-0.5 text-sm font-semibold text-neutral-900">{title}</h3>
  </div>
);

const FromYourBasketsCard = () => (
  <div className="rounded-lg border border-neutral-200 bg-white p-3">
    <p className="text-xs text-neutral-500">From your baskets</p>
    <div className="mt-2 flex items-center gap-2 min-w-0">
      <BasketGlyph />
      <p className="min-w-0 font-medium text-black">bleeding</p>
    </div>
    <p className="mt-2 text-sm font-semibold text-neutral-900">Vitamin C deficiency</p>
  </div>
);

const CompactBasketRow = ({ emphasised = false }) => (
  <div
    className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-white px-3 py-2 text-sm ${
      emphasised ? 'border-2 border-neutral-900' : 'border border-neutral-200'
    }`}
  >
    <span className="inline-flex items-center gap-2">
      <BasketGlyph />
      <span className="font-medium text-black">bleeding</span>
    </span>
    <span className="text-neutral-400" aria-hidden="true">·</span>
    <span className="text-neutral-900">Vitamin C deficiency</span>
  </div>
);

const STEP_ENTRANCE_MS = 300;
const STEP_STAGGER_MS = 150;
const STOP_STAGGER_MS = 250;
const STOP_START_MS = STEP_STAGGER_MS * 2 + STEP_ENTRANCE_MS;

const entranceClassName = (isShown) =>
  [
    'transition-all duration-300 ease-out',
    'motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0',
    isShown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
  ].join(' ');

const HowATopicComesBack = () => {
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [figureRef, isInView] = useInView({ enabled: !prefersReducedMotion });
  const isShown = isInView || prefersReducedMotion;

  return (
  <figure
    ref={figureRef}
    aria-label="Example: a topic in a basket keeps coming back"
    className="rounded-xl border-2 border-neutral-900 bg-white p-4 sm:p-6"
  >
    <h2 className="text-lg font-semibold text-neutral-900">How a topic comes back</h2>
    <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)] md:gap-4">
      <div
        className={`min-w-0 ${entranceClassName(isShown)}`}
        style={{ transitionDelay: isShown ? '0ms' : undefined }}
      >
        <StepHeading number="1" title="Day 1: create a topic" />
        <div className="space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
          <ExampleField label="Topic" value="Vitamin C deficiency" />
          <ExampleField label="Hard detail" value="Increased bleeding tendency" />
        </div>
      </div>

      <div
        className={`min-w-0 ${entranceClassName(isShown)}`}
        style={{ transitionDelay: isShown ? `${STEP_STAGGER_MS}ms` : undefined }}
      >
        <StepHeading number="2" title="Put it in a basket" />
        <div className="space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
          <div className="flex items-center gap-2 min-w-0">
            <BasketGlyph />
            <p className="text-lg font-medium text-black">bleeding</p>
          </div>
          <div className="rounded-lg border border-neutral-300 bg-white px-3 py-2">
            <p className="text-sm font-semibold text-neutral-900">Vitamin C deficiency</p>
            <p className="mt-0.5 text-sm text-neutral-600">Increased bleeding tendency</p>
          </div>
          <p className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
            <Check size={12} aria-hidden="true" />
            Available for resurfacing
          </p>
        </div>
      </div>

      <div
        className={`min-w-0 ${entranceClassName(isShown)}`}
        style={{ transitionDelay: isShown ? `${STEP_STAGGER_MS * 2}ms` : undefined }}
      >
        <StepHeading number="3" title="Next topics: it keeps showing up" />
        <div className="rounded-lg border-2 border-neutral-900 bg-white p-3">
          <p className="text-base font-semibold text-neutral-900">
            Because it&apos;s in a basket, it shows up every time you look for a basket for a new topic. Even on exam day.
          </p>
          <ol className="mt-4 space-y-0">
            <li
              className={`grid grid-cols-[16px_minmax(0,1fr)] gap-3 ${entranceClassName(isShown)}`}
              style={{ transitionDelay: isShown ? `${STOP_START_MS}ms` : undefined }}
            >
              <div className="flex flex-col items-center" aria-hidden="true">
                <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-neutral-900" />
                <span className="mt-1 w-px flex-1 bg-neutral-300" />
              </div>
              <div className="min-w-0 pb-4">
                <p className="text-sm font-medium text-neutral-900">
                  Adding Asthma
                  <span className="ml-2 inline-flex align-middle rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-500">
                    Day 3
                  </span>
                </p>
                <div className="mt-2">
                  <FromYourBasketsCard />
                </div>
              </div>
            </li>
            <li
              className={`grid grid-cols-[16px_minmax(0,1fr)] gap-3 ${entranceClassName(isShown)}`}
              style={{ transitionDelay: isShown ? `${STOP_START_MS + STOP_STAGGER_MS}ms` : undefined }}
            >
              <div className="flex flex-col items-center" aria-hidden="true">
                <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-neutral-900" />
                <span className="mt-1 w-px flex-1 bg-neutral-300" />
              </div>
              <div className="min-w-0 pb-4">
                <p className="text-sm font-medium text-neutral-900">
                  Adding Gout
                  <span className="ml-2 inline-flex align-middle rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-500">
                    Day 90
                  </span>
                </p>
                <div className="mt-2">
                  <CompactBasketRow />
                </div>
              </div>
            </li>
            <li
              className={`grid grid-cols-[16px_minmax(0,1fr)] gap-3 ${entranceClassName(isShown)}`}
              style={{ transitionDelay: isShown ? `${STOP_START_MS + STOP_STAGGER_MS * 2}ms` : undefined }}
            >
              <div className="flex flex-col items-center" aria-hidden="true">
                <span className="mt-1 h-3.5 w-3.5 rounded-full bg-neutral-900" />
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold text-neutral-900">Exam day</p>
                <div className="mt-2">
                  <CompactBasketRow emphasised />
                </div>
                <p className="mt-2 text-sm text-neutral-700">Still there. Still familiar.</p>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </div>
  </figure>
  );
};

const EmptyBasketNames = ({ baskets }) => {
  if (!baskets.length) return null;

  const visibleNames = baskets.slice(0, BASKET_NAME_CAP).map((basket) => basket.name);
  const hiddenCount = baskets.length - visibleNames.length;

  return (
    <div className="space-y-1">
      <p className="text-sm leading-6 text-neutral-800 break-words">
        <span className="font-semibold text-neutral-900">
          Your baskets ({baskets.length}):
        </span>
        {' '}
        {visibleNames.join(' · ')}
        {hiddenCount > 0 ? ` · +${hiddenCount} more` : ''}
      </p>
      <p className="text-sm text-neutral-600">
        Add a topic and you can place it in one of these.
      </p>
    </div>
  );
};

/**
 * Teaching screen for a loaded workspace that has no topics yet.
 */
const EmptyWorkspace = ({ workspaceId, baskets = [], onAddFirstTopic }) => {
  const [draft, setDraft] = useState('');
  const viewedForWorkspaceRef = useRef(null);

  useEffect(() => {
    if (!workspaceId || viewedForWorkspaceRef.current === workspaceId) return;
    viewedForWorkspaceRef.current = workspaceId;
    captureConnectionsEvent('empty_state_viewed', {
      workspace_id: workspaceId,
      basket_count: baskets.length,
    });
  }, [workspaceId, baskets.length]);

  const handleDraftChange = (event) => {
    setDraft(event.target.value);
  };

  const handleAddFirstTopic = (event) => {
    event.preventDefault();
    const trimmed = draft.trim();
    captureConnectionsEvent('empty_state_add_topic_clicked', {
      workspace_id: workspaceId,
      had_text: trimmed.length > 0,
    });
    onAddFirstTopic?.(trimmed);
  };

  return (
    <div className="mx-auto w-full max-w-[920px] space-y-8">
      <div className="space-y-4">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-neutral-900 sm:text-3xl">
            Start with one topic.
          </h1>
          <p className="text-base text-neutral-600">
            Put it in a basket. It comes back every time you add another topic.
          </p>
        </div>

        <form
          onSubmit={handleAddFirstTopic}
          className="flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <div className="relative min-w-0 flex-1">
            <label htmlFor="empty-state-topic" className="sr-only">
              What are you studying now?
            </label>
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500"
              size={18}
              aria-hidden="true"
            />
            <input
              id="empty-state-topic"
              type="text"
              value={draft}
              onChange={handleDraftChange}
              placeholder="What are you studying now?"
              autoComplete="off"
              className="w-full min-h-[48px] rounded-xl border-2 border-neutral-900 bg-white py-3 pl-9 pr-4 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900"
            />
          </div>
          <button type="submit" className={PRIMARY_BUTTON_CLASS_NAME}>
            Add first topic
          </button>
        </form>

        <ul className="flex flex-wrap gap-x-4 gap-y-2">
          {REASSURANCES.map((item) => (
            <li key={item} className="inline-flex items-center gap-1.5 text-sm text-neutral-700">
              <Check size={16} className="shrink-0 text-emerald-700" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>

        <p className="border-l-[3px] border-neutral-300 pl-3 text-sm text-neutral-700">
          <span className="font-semibold text-neutral-900">Prefer not to start from scratch?</span>{' '}
          Import the MSRA workspace from the sidebar for ready-made topics. Many are useful for other medical exams, too.
        </p>

        <EmptyBasketNames baskets={baskets} />
      </div>

      <HowATopicComesBack />
    </div>
  );
};

export default EmptyWorkspace;
